import React from 'react';
import {
  Box, Grid, Paper, Typography, Button, TextField, Chip, LinearProgress,
  Divider, Link, InputAdornment, Tooltip, Select, MenuItem, FormControl,
  Autocomplete, ToggleButton, ToggleButtonGroup, IconButton,
} from '@mui/material';
import {
  CheckCircle, Warning, ErrorOutline, Refresh, DoneAll, TrendingUp,
  PhotoCamera, AutoFixHigh,
} from '@mui/icons-material';
import firebase from '../../utilities/firebase';
import moment from 'moment';
import { StateManager } from '../../utilities/stateManager';
import RequestManager from '../../utilities/requestManager';
import constants from '../../utilities/constants';
import { useSelectMenuProps, useAutocompleteSlotProps } from '../../utilities/ModalStackContext';

const fmt = (n) =>
  (typeof n === 'number' && !Number.isNaN(n))
    ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
    : '—';

// Mirror CarSummary's price-history behaviour: seed a baseline entry from the
// current price on first change, dedupe by calendar day, then append today's.
const buildPriceHistory = (car, value) => {
  const today = moment().format('YYYY/MM/DD');
  let history = [];
  if (!car.price_history || car.price_history.length === 0) {
    history.push({ price: car.price || 0, date: car.status_time || today });
  } else {
    history = [...car.price_history];
  }
  history = history.filter(entry => entry.date !== today);
  history.push({ price: value, date: today });
  return history;
};

// Photo sections + video fields on the car doc, for the listing-media readout.
const IMAGE_SECTIONS = [
  { key: 'ext_images',      label: 'Exterior' },
  { key: 'interior_images', label: 'Interior' },
  { key: 'engine_images',   label: 'Engine' },
  { key: 'under_images',    label: 'Undercarriage' },
  { key: 'stamping_images', label: 'Stampings' },
  { key: 'vin_image',       label: 'VIN' },
  { key: 'odometer_image',  label: 'Odometer' },
];
const VIDEO_FIELDS = [
  { key: 'youtube_link',        label: 'YouTube' },
  { key: 'walkaround_video',    label: 'Walkaround' },
  { key: 'engine_video',        label: 'Engine' },
  { key: 'driving_video',       label: 'Driving' },
  { key: 'convertible_video',   label: 'Convertible' },
  { key: 'undercarriage_video', label: 'Undercarriage' },
];
const mediaCount = (v) => (Array.isArray(v) ? v.length : (v ? 1 : 0));
const firstUrl = (v) => (Array.isArray(v) ? v[0] : v) || '';

const STATUS_ICON = {
  ok:   <CheckCircle fontSize="small" color="success" />,
  warn: <Warning fontSize="small" color="warning" />,
  fix:  <ErrorOutline fontSize="small" color="error" />,
};

// A review stored before the checklist carried its own editors still has to be
// editable, so the editor is inferred for the rows the old validator emitted.
const LEGACY_EDITORS = {
  year: 'text', make: 'text', model: 'text', trim: 'text', color: 'text',
  vin: 'text', miles: 'number', drivetrain: 'select',
};
const rowEditor = (d) => d.editor || LEGACY_EDITORS[d.field] || 'none';

// Options fall back to the app's own lists, so a legacy row still gets the
// right dropdown and a proposal can never render the Select blank.
const CONSTANT_OPTIONS = {
  drivetrain: () => constants.drivetrains,
  cylinders: () => constants.cylinderCounts.map(String),
  'body-style': () => constants.bodyStyles,
  'door-count': () => constants.doorCounts.map(String),
  transmission: () => constants.transmissions,
};
const rowOptions = (d) => {
  if (Array.isArray(d.options) && d.options.length) return d.options;
  const from = CONSTANT_OPTIONS[d.field];
  return from ? from() : [];
};

// What a row writes when it is applied. Every row but the mileage disclosure is
// one field; that one is three booleans, so it carries `applyAs`. A row whose
// `field` is synthetic and has no `applyAs` writes nothing — better an ignored
// proposal than a `miles_disclosure` field invented on the car.
const SYNTHETIC_FIELDS = ['miles_disclosure', 'ymmt', 'writeup', 'bullets'];
const writeFor = (d, value) => {
  if (d.applyAs) return { ...d.applyAs };
  if (SYNTHETIC_FIELDS.includes(d.field)) return {};
  return { [d.field]: value };
};

const CONFIDENCE_COLOR = { high: 'success', medium: 'info', low: 'default' };

export default function SalesReview(props) {
  const { car, updater } = props;
  const review = car.salesReview;

  const [busy, setBusy] = React.useState(false);
  const [price, setPrice] = React.useState('');
  // Which proposed detail fixes to apply on Complete (keyed by field). A row
  // applied by hand drops out of this, so Complete never re-writes it.
  const [applyFixes, setApplyFixes] = React.useState({});

  // Seed the editable price + fix toggles when a fresh proposal arrives.
  React.useEffect(() => {
    if (review && review.pricing) setPrice(String(review.pricing.recommendedPrice || ''));
    if (review && Array.isArray(review.details)) {
      const init = {};
      review.details.forEach(d => { if (d.proposed !== undefined) init[d.field] = true; });
      setApplyFixes(init);
    }
  }, [review && review.ranAt]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Saving a field ─────────────────────────────────────────────────
  // Optimistic: the row shows the new value at once and the write goes in the
  // background. Only a failure interrupts, because a person editing fifteen
  // fields should never wait on fifteen round trips.
  const saveField = React.useCallback((data) => {
    updater && updater({ ...car, ...data });
    firebase.firestore().collection('cars').doc(car.stock).set(data, { merge: true })
      .catch(err => {
        console.error('Sales review field save failed:', err);
        StateManager.setAlertAndOpen(`Could not save ${Object.keys(data).join(', ')}`, 'error');
      });
  }, [car, updater]);

  const rerun = async () => {
    setBusy(true);
    StateManager.setAlertAndOpen('Running sales review in the background…', 'info');
    const res = await RequestManager.post({ function: 'runSalesReview', variables: { stock: car.stock } });
    setBusy(false);
    if (res && res.success && res.salesReview) {
      updater && updater({ ...car, salesReview: res.salesReview });
      StateManager.setAlertAndOpen('Sales review updated', 'success');
    } else {
      StateManager.setAlertAndOpen('Sales review failed to run', 'error');
    }
  };

  // Every proposal the review still holds, written in one go.
  const applyAllProposals = () => {
    const pending = (review.details || [])
      .filter(d => d.proposed !== undefined && applyFixes[d.field])
      .map(d => writeFor(d, d.proposed))
      .filter(w => Object.keys(w).length > 0);
    if (pending.length === 0) return;
    saveField(pending.reduce((acc, w) => ({ ...acc, ...w }), {}));
    setApplyFixes({});
    StateManager.setAlertAndOpen(`Applied ${pending.length} proposal${pending.length === 1 ? '' : 's'}`, 'success');
  };

  const applyAndComplete = async () => {
    setBusy(true);
    try {
      const stock = car.stock;
      const newPrice = Number(price);
      const update = {};

      if (newPrice > 0) {
        update.price = newPrice;
        update.priceUpdated = true;
        if (newPrice !== car.price) update.price_history = buildPriceHistory(car, newPrice);
      }

      // Apply the checked detail fixes that have not already been applied by hand.
      (review.details || []).forEach(d => {
        if (d.proposed !== undefined && applyFixes[d.field]) Object.assign(update, writeFor(d, d.proposed));
      });

      // Advance the pipeline to complete AND activate the car so it's listable.
      update.sub_status = 'complete';
      update.sub_status_time = moment().format('YYYY/MM/DD');
      update.status = 'active';
      update.status_time = moment().format('YYYY/MM/DD');
      if (!car.activated_date) update.activated_date = moment().format('YYYY/MM/DD');
      update.salesReview = {
        ...review,
        status: 'applied',
        appliedAt: moment().format('YYYY/MM/DD HH:mm'),
        appliedPrice: newPrice,
      };

      await firebase.firestore().collection('cars').doc(stock).set(update, { merge: true });
      updater && updater({ ...car, ...update });

      StateManager.setAlertAndOpen(`Sales review complete — ${stock} set Active at ${fmt(newPrice)}`, 'success');
    } catch (e) {
      StateManager.setAlertAndOpen('Failed to apply sales review', 'error');
    } finally {
      setBusy(false);
    }
  };

  // ── Empty / running / failed states ──────────────────────────────────
  if (!review) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          No sales review yet. It runs automatically when a car enters the Sales Review
          stage — or run it now.
        </Typography>
        <Button variant="contained" startIcon={<TrendingUp />} onClick={rerun} disabled={busy}>
          Run Sales Review
        </Button>
      </Box>
    );
  }

  if (review.status === 'running') {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>Sales review in progress…</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Pricing the car against Hagerty + classic.com, reading the title and the VIN
          plate, and working out what this car should be called on a listing. This
          usually takes a few minutes.
        </Typography>
        <LinearProgress />
      </Box>
    );
  }

  if (review.status === 'failed' || review.ok === false) {
    return (
      <Box sx={{ p: 4 }}>
        <Paper variant="outlined" sx={{ p: 2, borderColor: 'error.main', mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} color="error.main" gutterBottom>Sales review failed</Typography>
          <Typography variant="body2" color="error.main">{review.error || 'Unknown error.'}</Typography>
        </Paper>
        <Button variant="outlined" startIcon={<Refresh />} onClick={rerun} disabled={busy}>Retry</Button>
      </Box>
    );
  }

  const p = review.pricing || {};
  const applied = review.status === 'applied';
  const details = review.details || [];
  const pendingCount = details.filter(d => d.proposed !== undefined && applyFixes[d.field]).length;
  const blocking = details.filter(d => d.status === 'fix').length;

  return (
    <Box sx={{ px: { xs: 1, sm: 3 }, py: 2, maxWidth: 900, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>

      {applied && (
        <Paper variant="outlined" sx={{ p: 1.5, borderColor: 'success.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          <DoneAll color="success" />
          <Typography variant="body2" color="success.dark">
            Applied {review.appliedAt} — listed at {fmt(review.appliedPrice)}.
          </Typography>
        </Paper>
      )}

      {/* ── Pricing ─────────────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={700}>Pricing</Typography>
          <Chip
            size="small"
            label={`confidence: ${p.confidence || 'low'}`}
            color={p.confidence === 'high' ? 'success' : p.confidence === 'medium' ? 'info' : 'default'}
            variant="outlined"
          />
        </Box>

        <Grid container spacing={1.5} sx={{ mb: 1 }}>
          <PriceStat label="Cost" value={fmt(p.cost)} />
          <PriceStat label={`Cost × ${p.markup || 1.25}`} value={fmt(p.costPrice)} highlight={p.basis === 'cost'} />
          <PriceStat label="Market (higher of the two)" value={fmt(p.marketPrice)} highlight={p.basis === 'market'} />
          <PriceStat label="Current price" value={fmt(p.currentPrice)} />
        </Grid>

        <Typography variant="caption" color="text.secondary">
          Hagerty {fmt(p.hagerty)} · classic.com {fmt(p.classicCom)}
          {(p.low || p.high) ? ` · range ${fmt(p.low)}–${fmt(p.high)}` : ''}
        </Typography>

        {p.reasoning && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>{p.reasoning}</Typography>
        )}

        {Array.isArray(p.sources) && p.sources.length > 0 && (
          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {p.sources.map((s, i) => (
              <Link key={i} href={s.url} target="_blank" rel="noopener" variant="caption">{s.label}</Link>
            ))}
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Asking price"
            value={price}
            onChange={e => setPrice(e.target.value.replace(/[^\d]/g, ''))}
            disabled={applied}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            sx={{ width: 200 }}
          />
          <Typography variant="caption" color="text.secondary">
            Recommended {fmt(p.recommendedPrice)} (from {p.basis === 'market' ? 'market value' : `cost × ${p.markup || 1.25}`}).
            Edit if your judgment differs.
          </Typography>
        </Box>
      </Paper>

      {/* ── Detail checklist ────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>Listing details</Typography>
            <Typography variant="caption" color="text.secondary">
              Every field here saves as you change it. {blocking > 0
                ? `${blocking} still need${blocking === 1 ? 's' : ''} attention before this car lists cleanly.`
                : 'Nothing is blocking the listing.'}
            </Typography>
          </Box>
          {!applied && pendingCount > 0 && (
            <Button size="small" variant="contained" startIcon={<AutoFixHigh />} onClick={applyAllProposals}>
              Apply {pendingCount} proposal{pendingCount === 1 ? '' : 's'}
            </Button>
          )}
        </Box>

        {review.listingSummary && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{review.listingSummary}</Typography>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {details.map((d, i) => (
            <DetailRow
              key={d.field + i}
              row={d}
              car={car}
              first={i === 0}
              applied={applied}
              saveField={saveField}
              queued={!!applyFixes[d.field]}
              onApplied={() => setApplyFixes(prev => ({ ...prev, [d.field]: false }))}
              onToggleQueue={() => setApplyFixes(prev => ({ ...prev, [d.field]: !prev[d.field] }))}
            />
          ))}
        </Box>

        {/* Photo counts per section + video presence */}
        <Divider sx={{ my: 1.5 }} />
        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Photos by section</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {IMAGE_SECTIONS.map(s => {
            const n = mediaCount(car[s.key]);
            return (
              <Chip key={s.key} size="small" label={`${s.label}: ${n}`}
                color={n > 0 ? 'success' : 'default'} variant={n > 0 ? 'filled' : 'outlined'} />
            );
          })}
        </Box>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Videos</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {VIDEO_FIELDS.map(v => {
            const has = mediaCount(car[v.key]) > 0;
            return (
              <Chip key={v.key} size="small" icon={has ? <CheckCircle /> : undefined} label={v.label}
                color={has ? 'success' : 'default'} variant={has ? 'filled' : 'outlined'} />
            );
          })}
        </Box>
      </Paper>

      {/* ── Actions ─────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button variant="outlined" startIcon={<Refresh />} onClick={rerun} disabled={busy}>Re-run</Button>
        <Button variant="contained" startIcon={<DoneAll />} onClick={applyAndComplete} disabled={busy || applied || !(Number(price) > 0)}>
          {applied ? 'Completed' : 'Apply & Complete'}
        </Button>
      </Box>
    </Box>
  );
}

// ── One checklist row ────────────────────────────────────────────────
// The row is the editor. The proposal sits beside it as a button, so accepting
// the review's answer and typing your own are the same gesture in the same place.

const DetailRow = (props) => {
  const { row, car, first, applied, saveField, queued, onApplied, onToggleQueue } = props;
  const editor = rowEditor(row);
  const options = rowOptions(row);
  const menuProps = useSelectMenuProps();
  const autocompleteSlots = useAutocompleteSlotProps();

  // The live value is the car's, not the review's — the review is a snapshot and
  // the car is the truth, including edits made since it ran.
  const live = row.field === 'miles_disclosure'
    ? currentDisclosure(car)
    : (car[row.field] === undefined || car[row.field] === null ? '' : String(car[row.field]));

  const [draft, setDraft] = React.useState(live);
  React.useEffect(() => { setDraft(live); }, [live]);

  const commit = (value) => {
    if (SYNTHETIC_FIELDS.includes(row.field)) return; // not a field on the car
    if (String(value) === String(live)) return;
    saveField({ [row.field]: value });
  };

  const chooseDisclosure = (value) => {
    if (!value) return;
    const flags = (row.flagsByValue && row.flagsByValue[value]) || DISCLOSURE_FLAGS[value];
    if (flags) saveField({ ...flags });
    if (row.proposedValue === value) onApplied();
  };

  const acceptProposal = () => {
    if (row.field === 'miles_disclosure') {
      chooseDisclosure(row.proposedValue);
      return;
    }
    const write = writeFor(row, row.proposed);
    if (Object.keys(write).length === 0) return;
    saveField(write);
    onApplied();
  };

  const proposalOpen = row.proposed !== undefined && !applied
    && String(row.proposed) !== String(row.field === 'miles_disclosure' ? disclosureLabel(live) : live);

  return (
    <Box sx={{ py: 1.25, borderTop: first ? 'none' : '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box sx={{ mt: 1.25 }}>{STATUS_ICON[row.status] || STATUS_ICON.ok}</Box>

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" fontWeight={600}>{row.label}</Typography>
            {row.required && <Chip size="small" label="required" variant="outlined" sx={{ height: 18, fontSize: 11 }} />}
            {row.confidence && (
              <Chip size="small" variant="outlined" label={row.confidence}
                color={CONFIDENCE_COLOR[row.confidence] || 'default'} sx={{ height: 18, fontSize: 11 }} />
            )}
          </Box>

          {/* The editor */}
          <Box sx={{ mt: 0.75, maxWidth: 420 }}>
            {editor === 'none' && (
              <Typography variant="body2">{live || <em>(empty)</em>}</Typography>
            )}

            {(editor === 'text' || editor === 'number') && (
              <TextField
                size="small" fullWidth value={draft} disabled={applied}
                onChange={e => setDraft(editor === 'number'
                  ? e.target.value.replace(/[^\d]/g, '')
                  : (row.field === 'vin' ? e.target.value.toUpperCase() : e.target.value))}
                onBlur={() => commit(draft.trim())}
                onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
              />
            )}

            {editor === 'select' && (
              <FormControl size="small" fullWidth disabled={applied}>
                <Select
                  displayEmpty MenuProps={menuProps} value={options.includes(live) ? live : ''}
                  onChange={e => commit(e.target.value)}
                >
                  <MenuItem value=""><em>(not set)</em></MenuItem>
                  {options.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
            )}

            {/* Free text with the vocabulary the listing sites index as
                suggestions. Commit on blur of the whole control, so MUI keeps
                its own handler on the input and the popup still closes. */}
            {editor === 'suggest' && (
              <Autocomplete
                freeSolo size="small" options={options} slotProps={autocompleteSlots}
                disabled={applied} inputValue={draft}
                onInputChange={(e, v) => setDraft(v || '')}
                onChange={(e, v) => commit((v || '').trim())}
                onBlur={() => commit(draft.trim())}
                renderInput={params => <TextField {...params} />}
              />
            )}

            {editor === 'disclosure' && (
              <ToggleButtonGroup
                exclusive size="small" value={live} disabled={applied}
                onChange={(e, v) => chooseDisclosure(v)}
              >
                {(row.options || DISCLOSURE_OPTIONS).map(o => (
                  <ToggleButton key={o.value} value={o.value} sx={{ textTransform: 'none' }}>{o.label}</ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}
          </Box>

          {/* What the review read, and where */}
          {Array.isArray(row.sources) && row.sources.length > 0 && (
            <Box sx={{ mt: 0.75, display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
              {row.sources.map((s, i) => (
                <Chip key={i} size="small" variant="outlined"
                  color={String(s.vin).toUpperCase() === live.toUpperCase() ? 'success' : 'warning'}
                  label={`${s.label}: ${s.vin}`} sx={{ fontSize: 11 }} />
              ))}
              {row.field === 'vin' && firstUrl(car.vin_image) && (
                <Tooltip title="Open the VIN photo">
                  <IconButton size="small" component="a" target="_blank" rel="noopener" href={firstUrl(car.vin_image)}>
                    <PhotoCamera fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}

          {row.note && (
            <Typography
              variant="caption" component="div" sx={{ mt: 0.5 }}
              color={row.status === 'ok' ? 'text.secondary' : row.status === 'fix' ? 'error.main' : 'warning.dark'}
            >
              {row.note}
            </Typography>
          )}
        </Box>

        {/* The proposal, as one click */}
        {proposalOpen && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, pt: 0.5 }}>
            <Tooltip title={`Set ${row.label} to "${row.proposed}"`}>
              <Button size="small" variant="contained" onClick={acceptProposal} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
                Use “{row.proposed}”
              </Button>
            </Tooltip>
            <Chip
              size="small"
              label={queued ? 'On complete' : 'Skipped'}
              color={queued ? 'success' : 'default'}
              variant={queued ? 'filled' : 'outlined'}
              onClick={onToggleQueue}
              sx={{ height: 20, fontSize: 11 }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

// The three booleans the disclosure writes, mirrored from
// sales_review/facts.js so a row saved before the flags existed still applies.
const DISCLOSURE_FLAGS = {
  actual:     { is_actual: true,  is_exempt: false, is_excess: false },
  not_actual: { is_actual: false, is_exempt: false, is_excess: false },
  exceeds:    { is_actual: false, is_exempt: false, is_excess: true  },
  exempt:     { is_actual: false, is_exempt: true,  is_excess: false },
};
const DISCLOSURE_OPTIONS = [
  { value: 'actual', label: 'Actual Miles' },
  { value: 'exempt', label: 'Miles Exempt' },
  { value: 'not_actual', label: 'Not Actual' },
  { value: 'exceeds', label: 'Exceeds Limits' },
];
// Reads the same order as facts.currentDisclosure on the backend, so the row's
// `current` and the toggle below it never disagree.
const currentDisclosure = (car) => (
  car.is_exempt ? 'exempt'
    : car.is_excess ? 'exceeds'
      : car.is_actual === true ? 'actual'
        : car.is_actual === false ? 'not_actual'
          : ''
);
const disclosureLabel = (value) => {
  const found = DISCLOSURE_OPTIONS.find(o => o.value === value);
  return found ? found.label : '';
};

const PriceStat = ({ label, value, highlight }) => (
  <Grid item xs={6} sm={3}>
    <Paper
      variant="outlined"
      sx={{ p: 1.5, textAlign: 'center', borderColor: highlight ? 'success.main' : 'divider', bgcolor: highlight ? 'success.light' : 'transparent' }}
    >
      <Typography variant="caption" color="text.secondary" component="div">{label}</Typography>
      <Typography variant="h6" fontWeight={700}>{value}</Typography>
    </Paper>
  </Grid>
);
